<template>
  <div id="container" v-bind:class="getContainerClass()">
    <div
      id="map"
      v-bind:class="this.mapStyleClass"
      ref="map"
      width="100%"
      height="100%"
      v-on:click="onMapClicked()"
    ></div>
    <a v-if="!this.hideOndagoLogo" href="https://www.ondagoapp.com" target="_blank">
      <img id="ondagoLogo" class="ondago-logo-full" src="@/assets/ondago_logo_gradient.png" />
      <img id="ondagoLogo" class="ondago-logo-pin" src="@/assets/logo.png" />
    </a>
    <div id="searchBar" v-if="!this.disableSearch">
      <a
        v-if="customSearchButtonLabel"
        class="custom-search-button"
        href="#"
        v-on:click="onSearchClicked()"
        >{{ customSearchButtonLabel }}</a
      >
      <a v-else href="#" v-on:click="onSearchClicked()"
        ><img src="@/assets/buttons/searchActionButton.png"
      /></a>
      <a v-if="homeBounds" class="custom-search-button" href="#" @click="onHomeClicked()">{{
        $t('map_as_map.recenter_button')
      }}</a>
    </div>
    <div v-if="this.loaded && this.mapSpec == undefined" style="position: absolute; top: 0px; left: 0px; margin: auto; width: 100%; height: 100%;">
      <div style="margin: 200px 0px;">{{ this.$i18n.t("map.map_not_found") }}</div>
    </div>
    <div id="rightMiddleActionBar">
      <a href="#" v-on:click="onZoomInClicked()"
        ><img src="@/assets/buttons/zoomInActionButton.png"
      /></a>
      <a href="#" v-on:click="onZoomOutClicked()"
        ><img src="@/assets/buttons/zoomOutActionButton.png"
      /></a>
    </div>
    <div id="bottomLeftActionBar" ref="bottomLeftActionBar"></div>
    <div id="bottomRightActionBar">
      <a href="#" v-if="layersButtonVisible" v-on:click="onLayersClicked()"
        ><img src="@/assets/buttons/layersButton.png"
      /></a>
    </div>
    <div id="attributions" v-html="attributionsHTML"></div>
    <transition name="slide-fade-from-left">
      <MapSearchPanel
        id="searchPanel"
        v-if="(!this.disableLeftPanel && searchPanelVisible) || this.searchAlwaysVisible"
        v-bind:searchableObjects="this.searchableObjects"
        v-bind:mapObjectStatusTemplates="this.mapObjectStatusTemplates"
        v-bind:allMapObjectStatus="this.allMapObjectStatus"
        v-bind:searchPanelStructure="this.searchPanelStructure"
        v-bind:sortMethod="this.sortMethod"
        v-bind:projection="this.projection"
        v-on:searchable-object-selected="onSearchableObjectSelected"
      ></MapSearchPanel>
    </transition>
    <transition name="slide-fade-from-left">
      <MapElementDetailPanel
        id="detailPanel"
        v-if="!this.disableLeftPanel && detailPanelVisible"
        v-bind:detailSpec="selectedMapElementDetailSpec"
      ></MapElementDetailPanel>
    </transition>
    <transition name="slide-fade-from-left">
      <HtmlContentPanel
        id="htmlContentPanel"
        v-if="customActionHtmlUrl"
        v-bind:url="customActionHtmlUrl"
      >
      </HtmlContentPanel>
    </transition>
    <transition name="slide-fade-from-right">
      <MapLayersPanel
        id="layersPanel"
        v-if="layersPanelVisible"
        v-bind:mapLayers="this.mapSpec.mapLayers"
      >
      </MapLayersPanel>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import MapSearchPanel from './MapSearchPanel.vue';
import MapElementDetailPanel from './MapElementDetailPanel.vue';
import MapLayersPanel from './MapLayersPanel.vue';
import HtmlContentPanel from './HtmlContentPanel.vue';
import MapSpec from '@/map/MapSpec';
import MapboxMapControl from '@/ui/map/MapboxMapControl';
import MapElementDataStream from '@/map/stream/MapElementDataStream';
import Annotation from '@/map/element/Annotation';
import MapElementDetailSpec from '@/map/detail/MapElementDetailSpec';
import MapService from '@/service/MapService';
import LangUtils from '@/utils/LangUtils';
import { waitFor } from '@/utils/promiseUtils';
import UUIDUtils from '@/utils/UUIDUtils';

export default {
  components: {
    MapSearchPanel,
    MapElementDetailPanel,
    MapLayersPanel,
    HtmlContentPanel,
  },
  name: 'Map',
  data: () => {
    return {
      mapService: new MapService(),
      mapControl: null,
      searchPanelVisible: false,
      detailPanelVisible: false,
      layersPanelVisible: false,
      layersButtonVisible: false,
      attributionsHTML: null,
      selectedMapElement: null,
      highlightedMapElements: [],
      allMapObjectStatus: [],
      mapObjectStatusTemplates: [],
      customActionHtmlUrl: null,
      loaded: false
    };
  },
  props: {
    mapSpec: MapSpec,
    disableScrollZoom: Boolean,
    disableSearch: Boolean,
    disableLeftPanel: Boolean,
    hideOndagoLogo: Boolean,
    searchAlwaysVisible: Boolean,
    customSearchButtonLabel: String,
    homeBounds: Object,
  },
  computed: {
    mapStyleClass() {
      if (this.searchAlwaysVisible) {
        return 'search-always-opened-map';
      } else {
        return '';
      }
    },

    searchableObjects() {
      return this.mapSpec ? this.mapSpec.searchableObjects : [];
    },

    searchPanelStructure() {
      return this.mapSpec ? this.mapSpec.getSearchPanelStructure() : null;
    },
    sortMethod() {
      return this.mapSpec ? this.mapSpec.sortMethod : null;
    },
    projection() {
      return this.mapSpec ? this.mapSpec.projection : null;
    },

    selectedMapElementDetailSpec() {
      if (this.selectedMapElement) {
        const mapObjectStatus = this._findMapObjectStatusForMapElement(this.selectedMapElement.id);
        var mapObjectStatusTemplate = null;
        if (mapObjectStatus != null) {
          mapObjectStatusTemplate = this._findMapObjectStatusTemplate(
            mapObjectStatus.mapObjectStatusTemplateId
          );
        }
        return new MapElementDetailSpec(
          this.selectedMapElement,
          this.mapSpec.mapBundleId,
          mapObjectStatus,
          mapObjectStatusTemplate
        );
      }
      return null;
    },
  },
  watch: {
    mapSpec: function () {
      this.$forceUpdate();
      this.refresh();
    },
    homeBounds: function (value) {
      if (value) {
        this.homeBounds = value;
        const { topLeft, bottomRight } = value;
        this.mapControl?.centerOnBounds(topLeft, bottomRight);
      }
    },
    mapControl: function (ctl) {
      if (ctl && this.homeBounds) {
        const { topLeft, bottomRight } = this.homeBounds;
        ctl.centerOnBounds(topLeft, bottomRight);
      }
    },
  },
  methods: {
    onHomeClicked() {
      if (this.homeBounds) {
        const { topLeft, bottomRight } = this.homeBounds;
        this.mapControl?.centerOnBounds(topLeft, bottomRight);
      }
    },

    getActionIconUrl(action) {
      return this.mapService.buildUrlToAsset(
        this.mapSpec.mapBundleId,
        LangUtils.valueForLanguage(action.iconFilePath, this.$i18n.locale)
      );
    },

    getContainerClass() {
      if (
        (!this.disableLeftPanel && this.searchPanelVisible) ||
        (!this.disableLeftPanel && this.detailPanelVisible)
      ) {
        return 'opened-left-panel';
      } else if (this.layersPanelVisible) {
        return 'opened-right-panel';
      } else {
        return 'closed-left-panel closed-right-panel';
      }
    },

    refresh() {
      if (this.mapSpec) {
        const container = this.$refs['map'];
        if (container) {
          this.mapControl = new MapboxMapControl(this.mapSpec, container);
          this.mapControl.onClick((selectedFeatures) => {
            this.onFeaturesSelected(selectedFeatures);
          });
          this.mapControl.onMouseMove((mouseOverFeatures) => {
            this.onMouseOverFeatures(mouseOverFeatures);
          });
          this.mapControl.load();

          if (this.disableScrollZoom) {
            this.mapControl.disableScrollZoom();
          } else {
            this.mapControl.enableScrollZoom();
          }

          this.layersButtonVisible = this.mapSpec.mapLayers.length > 0;

          if (this.mapSpec.attributionsHTMLUrl) {
            axios
              .get(this.mapSpec.attributionsHTMLUrl)
              .then((response) => {
                if (response) {
                  this.attributionsHTML = response.data;
                }
              })
              .catch(() => {
                // TODO Unable to load attributions. Manage the error.
              });
          }
          // this.attributionsHTML = this.mapSpec.attributionsHTML;

          this.mapSpec.mapObjectStatusDataStream.observe().subscribe((data) => {
            if (data) {
              this.mapObjectStatusTemplates = data.mapObjectStatusTemplates;
              this.allMapObjectStatus = data.mapObjectStatusList;
              this.$forceUpdate();
            }
          });

          this._renderCustomActionsIcons();
        }
      }
    },

    onZoomInClicked() {
      this.mapControl.zoomIn();
    },

    onZoomOutClicked() {
      this.mapControl.zoomOut();
    },

    onLayersClicked() {
      this.searchPanelVisible = false;
      this.layersPanelVisible = true;
    },

    onSearchClicked() {
      this.layersPanelVisible = false;
      this.searchPanelVisible = true;
    },

    onMapClicked() {
      this.searchPanelVisible = false;
      this.layersPanelVisible = false;
    },

    onFeaturesSelected(features) {
      var selectedFeature = null;
      for (const feature of features) {
        if (feature.properties.interactive) {
          if (feature.properties.type == 'annotation') {
            selectedFeature = feature;
            break;
          } else {
            selectedFeature = feature;
          }
        }
      }

      this.selectMapElement(selectedFeature ? selectedFeature.properties.id : null, false, true);
    },

    onSearchableObjectSelected(searchableObjectId) {
      this.selectMapElement(searchableObjectId, true);
    },

    onMouseOverFeatures(mouseOverFeatures) {
      const hoveredMapElements = [];
      var hasAnnotations = false;
      for (const feature of mouseOverFeatures) {
        this._findMapElementById(feature.properties.id).then((mapElement) => {
          if (mapElement.isInteractive()) {
            hoveredMapElements.push(mapElement);
            if (mapElement instanceof Annotation) {
              hasAnnotations = true;
            }
          }
        });
      }

      this.highlightedMapElements = this.highlightedMapElements.filter((element) => {
        if (element instanceof Annotation || !hasAnnotations) {
          for (const feature of mouseOverFeatures) {
            if (element.getId() == feature.properties.id) {
              return true;
            }
          }
        }

        element.setHighlighted(false);

        return false;
      });

      if (hoveredMapElements.length > 0) {
        this.mapControl.changeMouseCursor('pointer');
      } else {
        this.mapControl.changeMouseCursor('');
      }

      for (const mapElement of hoveredMapElements) {
        if (mapElement instanceof Annotation || hasAnnotations == false) {
          mapElement.setHighlighted(true);
          this.highlightedMapElements.push(mapElement);
        }
      }
    },

    onCustomActionClicked(action) {
      if (action.type == 'showHtml') {
        const filePath = LangUtils.valueForLanguage(action.htmlFilePath, this.$i18n);
        if (filePath) {
          const completeUrl = this.mapService.buildUrlToAsset(this.mapSpec.mapBundleId, filePath);
          if (completeUrl) {
            this.customActionHtmlUrl = completeUrl;
          }
        }
      }
    },

    selectMapElement(mapElementId, centerOnMapElement, fireEvent = true, forceSelect = true) {
      if (mapElementId) {
        this.searchPanelVisible = false;

        this._findMapElementById(mapElementId).then((mapElement) => {
          if (mapElement.masterMapElement) {
            mapElement = mapElement.masterMapElement;
          }
          if (mapElement && (forceSelect || mapElement.isInteractive())) {
            if (this.selectedMapElement != null) {
              this._deepSetMapElementSelected(this.selectedMapElement, false);
              this.mapControl.hideSelectionMarker();
            }

            this._deepSetMapElementSelected(mapElement, true);

            if (fireEvent) {
              this.$root.$emit('onMapElementSelected', { id: mapElement.id });

              var event = {
                type: 'objectSelected',
                id: mapElement.id,
              };
              if (window.parent) {
                window.parent.postMessage(event, '*');
              }
            }
            this.selectedMapElement = mapElement;

            if (centerOnMapElement) {
              this.mapControl.centerOnMapElement(mapElement);
            }

            if (mapElement instanceof Annotation) {
              this.mapControl.showSelectionMarkerForMapElement(mapElement);
            }

            this.detailPanelVisible = true;
            this.layersPanelVisible = false;
            this.customActionHtmlUrl = null;
          }
        });
      } else {
        if (this.selectedMapElement != null) {
          this._deepSetMapElementSelected(this.selectedMapElement, false);
        }

        this.mapControl.hideSelectionMarker();

        this.detailPanelVisible = false;
        this.layersPanelVisible = false;
        this.customActionHtmlUrl = null;
      }
    },

    setMapElementVisibility(mapElementId, visible) {
      if (mapElementId) {
        this._findMapElementById(mapElementId).then((mapElement) => {
          mapElement.setHidden(!visible);
        });
      }
    },

    _findMapElementById(mapElementId, nTries = 3) {
      const promise = new Promise((resolve, reject) => {
        var matched = false;
        for (const dataStream of this.mapSpec.dataStreams) {
          if (dataStream instanceof MapElementDataStream) {
            for (const mapElement of dataStream.mapElements) {
              if (mapElement.getId() == mapElementId) {
                matched = true;
                resolve(mapElement);
              }
            }
          }
        }
        if (!matched) {
          reject(`Object ${mapElementId} not Found in map`);
        }
      });

      return promise.catch((err) => {
        console.error(err);
        if (nTries > 0) {
          return waitFor(1000).then(() => {
            return this._findMapElementById(mapElementId, nTries - 1);
          });
        }
      });
    },

    _findMapObjectStatusForMapElement(mapElementId) {
      if (this.allMapObjectStatus) {
        for (var mapObjectStatus of this.allMapObjectStatus) {
          if (mapObjectStatus.mapObjectId == mapElementId) {
            return mapObjectStatus;
          }
        }
      }
      return null;
    },

    _findMapObjectStatusTemplate(mapObjectStatusTemplateId) {
      if (this.mapObjectStatusTemplates) {
        for (var mapObjectStatusTemplate of this.mapObjectStatusTemplates) {
          if (mapObjectStatusTemplate.id == mapObjectStatusTemplateId) {
            return mapObjectStatusTemplate;
          }
        }
      }
      return null;
    },

    _deepSetMapElementSelected(mapElement, selected) {
      mapElement.setSelected(selected);
      if (mapElement.children && mapElement.children.length > 0) {
        for (var child of mapElement.children) {
          this._deepSetMapElementSelected(child, selected);
        }
      }
    },

    _renderCustomActionsIcons() {
      if (this.mapSpec && this.mapSpec.customActions) {
        const actionBar = this.$refs['bottomLeftActionBar'];

        for (var action of this.mapSpec.customActions) {
          const link = document.createElement('a');
          if (action.id) {
            link.id = action.id;
          } else {
            link.id = UUIDUtils.generateUUID();
            action.id = link.id;
          }
          link.className = 'custom-action';
          link.href = '#';

          link.onclick = () => {
            var foundAction = null;
            for (var action of this.mapSpec.customActions) {
              if (action.id == link.id) {
                foundAction = action;
                break;
              }
            }

            if (foundAction != null) {
              this.onCustomActionClicked(foundAction);
            }
          };

          const canvas = document.createElement('canvas');
          canvas.width = 50;
          canvas.height = 50;
          const context = canvas.getContext('2d');
          const centerX = canvas.width / 2;
          const centerY = canvas.height / 2;
          const radius = 25;

          context.beginPath();
          context.arc(centerX, centerY, radius, 0, 2 * Math.PI);
          context.fillStyle = '#224460';
          context.fill();

          const image = new Image();
          image.onload = function () {
            const offscreenCanvas = document.createElement('canvas');
            offscreenCanvas.width = 50;
            offscreenCanvas.height = 50;
            const offscreenContext = offscreenCanvas.getContext('2d');

            const x = canvas.width / 2 - image.width / 2;
            const y = canvas.height / 2 - image.height / 2;
            offscreenContext.drawImage(image, x, y);

            offscreenContext.globalCompositeOperation = 'source-in';

            offscreenContext.fillStyle = '#ffffff';
            offscreenContext.fillRect(0, 0, canvas.width, canvas.height);

            context.drawImage(offscreenCanvas, 0, 0);
          };

          image.src = this.mapService.buildUrlToAsset(
            this.mapSpec.mapBundleId,
            LangUtils.valueForLanguage(action.iconFilePath, this.$i18n)
          );

          link.append(canvas);

          actionBar.append(link);
        }
      }
    },
  },

  mounted() {
    // Safari has a problem with arrow functions. Since arrow function is required here to access this, 
    // we use a closure to properly access it in the timeout function.
    const outerThis = this;
    setTimeout(function() {
      outerThis.loaded = true;
    }, 5000)
  }
};
</script>

<style>
.mapboxgl-ctrl-group {
  background: transparent !important;
  box-shadow: none !important;
}

.mapboxgl-ctrl-bottom-right {
  bottom: 30px !important;
}

.mapboxgl-ctrl-geolocate {
  background: transparent !important;
  background-image: url(~@/assets/buttons/userLocationLockedActionButton.png) !important;
  background-size: 50px !important;
  width: 50px !important;
  height: 50px !important;
  background-color: transparent !important;
  background-repeat: no-repeat !important;
  border: none !important;
}

.custom-action {
  margin-right: 8px;
}
</style>

<style lang="scss" scoped>
#container {
  position: relative;
  width: 100%;
  height: 100%;
}

.search-always-opened-map {
  margin-left: 350px;
}

// .opened-left-panel {
//     transition: all 0.6s;
//     padding-left: 350px;
// }

.closed-left-panel {
  transition: all 0.5s;
  padding-left: 0px;
}

.opened-right-panel {
  transition: all 0.6s;
  padding-right: 0px;
}

.closed-right-panel {
  transition: all 0.5s;
  padding-right: 0px;
}

.ondago-logo-pin {
  display: none;
}

.custom-search-button {
  background-color: #224460;
  border-radius: 8px;
  color: #ffffff;
  margin-top: 20px;
  padding: 8px 20px;
  text-decoration: none;
}

#map {
  height: 100%;
}

#searchBar {
  position: absolute;
  top: 8px;
  left: 0%;
  margin-top: 10px;
  margin-left: 10px;
}

#searchBar a {
  margin-right: 8px;
  font-size: 12pt;
}

#searchPanel {
  background-color: #ffffff;
  box-shadow: 5px 0px #88888855;
  position: absolute;
  top: 0%;
  left: 0%;
  width: 350px;
  height: 100%;
}

#detailPanel {
  background-color: #ffffff;
  box-shadow: 5px 0px #88888855;
  position: absolute;
  top: 0%;
  left: 0%;
  width: 350px;
  height: 100%;
}

#layersPanel {
  background-color: #ffffff;
  box-shadow: 5px 0px #88888855;
  position: absolute;
  top: 0%;
  right: 0%;
  width: 350px;
  height: 100%;
}

#htmlContentPanel {
  background-color: #ffffff;
  box-shadow: 5px 0px #88888855;
  position: absolute;
  top: 0%;
  left: 0%;
  width: 350px;
  height: 100%;
}

#rightMiddleActionBar {
  position: absolute;
  top: 50%;
  left: 100%;
  margin-left: -60px;
  margin-top: -50px;
  width: 50px;
}

#rightMiddleActionBar a img {
  width: 50px;
  height: 50px;
}

#bottomLeftActionBar {
  position: absolute;
  bottom: 50px;
  left: 10px;
}

#bottomRightActionBar {
  position: absolute;
  bottom: 100px;
  right: 10px;
}

#bottomRightActionBar img {
  width: 50px;
  height: 50px;
}

#ondagoLogo {
  position: absolute;
  top: 100%;
  left: 0%;
  margin-top: -40px;
  margin-left: 10px;
  height: 30px;
}

#attributions {
  position: absolute;
  top: 100%;
  right: 0%;
  margin-top: -40px;
  margin-right: 10px;
}

.slide-fade-from-left-enter-active {
  transition: all 0.5s ease;
}

.slide-fade-from-left-leave-active {
  transition: all 0.5s ease;
}

.slide-fade-from-left-enter,
.slide-fade-from-left-leave-to

/* .slide-fade-from-left-leave-active below version 2.1.8 */ {
  transform: translateX(-350px);
}

.slide-fade-from-right-enter-active {
  transition: all 0.5s ease;
}

.slide-fade-from-right-leave-active {
  transition: all 0.5s ease;
}

.slide-fade-from-right-enter,
.slide-fade-from-right-leave-to

/* .slide-fade-from-right-leave-active below version 2.1.8 */ {
  transform: translateX(350px);
}

@media only screen and (max-width: 600px) {
  .ondago-logo-full {
    display: none;
  }

  .ondago-logo-pin {
    display: inline;
  }

  #rightMiddleActionBar {
    display: none;
  }

  .slide-fade-from-left-enter,
  .slide-fade-from-left-leave-to {
    transform: translateX(-80%);
  }

  .slide-fade-from-right-enter,
  .slide-fade-from-right-leave-to {
    transform: translateX(80%);
  }

  #searchPanel {
    width: 80%;
  }

  #detailPanel {
    width: 80%;
  }

  #htmlContentPanel {
    width: 80%;
  }

  .opened-left-panel {
    padding-left: 0px;
  }
}
</style>
